import { AdSlotType } from '../../../advertising/AdTypes';
import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { DEG_TO_RAD } from '../../../utils/math';
import { ConnectorProfile, ConnectorRole } from '../../connectors/ConnectorDefinition';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, moduleSides, obstacleRow, standardLanes } from '../segmentBuilders';
import { SegmentKind, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_STRAIGHT_20M – base module of the track system.
 *
 *   Cross-section (x):  side area | fence ±4.2 | curb ±3.3..3.6 | 3 lanes à 2.2 m (−3.3 … +3.3)
 *   Length (z):         CONNECTOR_IN z=0  →  CONNECTOR_OUT z=20
 *   Obstacle rows:      A z=5, C z=10, B z=15  (sparse = [C], dense = [A, B])
 *
 * Mirrors docs/level-design/SEGMENT_STANDARD.md – keep both in sync.
 */
const LENGTH = WORLD_CONFIG.baseSegmentLength;
const HALF_LENGTH = LENGTH / 2;
const FENCE_X = 4.2;
const AD_SURFACE_OFFSET = 0.08; // ad plane sits in front of the fence panel
const EVENT_SIDE_X = 9;
const FACE_RIGHT = 90 * DEG_TO_RAD;
const FACE_LEFT = -90 * DEG_TO_RAD;

const ALL_GROUND_OBSTACLES = [
  ObstacleType.Jump,
  ObstacleType.Slide,
  ObstacleType.DoubleJump,
  ObstacleType.LaneChange,
] as const;

export const SEG_STRAIGHT_20M: SegmentDefinition = {
  id: 'SEG_STRAIGHT_20M',
  kind: SegmentKind.Straight,
  displayName: 'Gerade 20 m',
  length: LENGTH,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',

  entry: {
    id: 'CONNECTOR_IN',
    role: ConnectorRole.In,
    profile: ConnectorProfile.Ground3Lane,
    transform: { x: 0, y: 0, z: 0, yaw: 0 },
    allowedNextProfiles: [],
  },
  exits: [
    {
      id: 'CONNECTOR_OUT',
      role: ConnectorRole.Out,
      profile: ConnectorProfile.Ground3Lane,
      transform: { x: 0, y: 0, z: LENGTH, yaw: 0 },
      allowedNextProfiles: [ConnectorProfile.Ground3Lane],
    },
  ],

  lanes: standardLanes(WORLD_CONFIG),

  obstacleSpawnPoints: [
    ...obstacleRow(WORLD_CONFIG, 'A', 5, ALL_GROUND_OBSTACLES),
    ...obstacleRow(WORLD_CONFIG, 'C', 10, ALL_GROUND_OBSTACLES),
    ...obstacleRow(WORLD_CONFIG, 'B', 15, ALL_GROUND_OBSTACLES),
  ],
  obstacleRowLayouts: { sparse: ['C'], dense: ['A', 'B'] },

  collectibleSpawnPoints: collectibleLanes(WORLD_CONFIG, 1, LENGTH - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  // Roadside bays (_01/_02, x ±8.1, z 5 / 15), backdrop building (_04, x ±20) and crane (_05, x ±34) –
  // the site layout every straight module repeats per 20 m block (segmentBuilders.ts: SITE_LAYOUT).
  decorationSockets: moduleSides(LENGTH).decorationSockets,

  dynamicEventSockets: [
    {
      id: 'SOCKET_EVT_SIDE_L',
      position: { x: -EVENT_SIDE_X, y: 0, z: HALF_LENGTH },
      yaw: FACE_RIGHT,
      supportedEvents: [DynamicEventType.ForkliftCrossing, DynamicEventType.TruckArrival, DynamicEventType.ExcavatorSwing],
      triggerDistance: 35,
      affectedLanes: [0, 1, 2],
    },
    {
      id: 'SOCKET_EVT_SIDE_R',
      position: { x: EVENT_SIDE_X, y: 0, z: HALF_LENGTH },
      yaw: FACE_LEFT,
      supportedEvents: [DynamicEventType.ForkliftCrossing, DynamicEventType.TruckArrival, DynamicEventType.ExcavatorSwing],
      triggerDistance: 35,
      affectedLanes: [0, 1, 2],
    },
    {
      id: 'SOCKET_EVT_OVERHEAD',
      position: { x: 0, y: 7, z: HALF_LENGTH },
      yaw: 0,
      supportedEvents: [DynamicEventType.CraneLoadSwing, DynamicEventType.PalletLift],
      triggerDistance: 40,
      affectedLanes: [0, 1, 2],
    },
    {
      id: 'SOCKET_EVT_TRACK',
      position: { x: 0, y: 0, z: HALF_LENGTH },
      yaw: 0,
      supportedEvents: [DynamicEventType.PitOpening, DynamicEventType.ScaffoldBlock, DynamicEventType.WallCollapse],
      triggerDistance: 30,
      affectedLanes: [0, 1, 2],
    },
  ],

  advertisingSlots: [
    {
      id: 'AD_FENCE_LEFT',
      slotType: AdSlotType.FenceLeft,
      position: { x: -(FENCE_X - AD_SURFACE_OFFSET), y: 1.15, z: HALF_LENGTH },
      yaw: FACE_RIGHT,
      width: 6,
      height: 1.5,
    },
    {
      id: 'AD_FENCE_RIGHT',
      slotType: AdSlotType.FenceRight,
      position: { x: FENCE_X - AD_SURFACE_OFFSET, y: 1.15, z: HALF_LENGTH },
      yaw: FACE_LEFT,
      width: 6,
      height: 1.5,
    },
  ],

  generation: {
    weight: 1,
    minDistance: 0,
    tags: ['straight', 'ground'],
  },
};
